/// <reference path="../../setup/_references.ts" />
module onvio.reports.firmManagementReports {
    'use strict';

    export interface IReportOptionsStateParams extends ng.ui.IStateParamsService {
        reportId: string;
        employeeReportOptionId: string;
    }

    export class FirmManagementReportsOptionsController implements bluemoon.reporting.IReportEditOptionsController {
        public reportId: string;
        public reportName: string;
        public backLinkUrl: string;
        employeeReportOptionId: string;
        employeeReportOptionsDto: cs.IEmployeeReportOptionV1Dto;
        reportOptionsDataConverter = new bmp.ReportOptionsDataConverter();

        static $inject = [
            '$stateParams',
            '$translate',
            'csReportOptionsV1',
            'csEmployeeReportOptionV1',
            'bmpCurrentEmployeeService',
            'bmpProviderUtils',
            'bmrcReportListService',
            'onvio.reports.firmManagementReports.FirmManagementReportsListService'
        ];

        constructor(
            private $stateParams: IReportOptionsStateParams,
            private $translate: ng.translate.ITranslateService,
            private csReportOptionsV1: cs.IReportOptionsV1Service,
            private csEmployeeReportOptionsV1: cs.IEmployeeReportOptionV1Service,
            private currentEmployeeService: bmp.ICurrentEmployeeService,
            private providerUtils: bmp.IProviderUtilsService,
            private reportListService: any,
            private firmManagementReportsListService: onvio.reports.firmManagementReports.FirmManagementReportsListService
        ) {
            this.reportId = $stateParams.reportId;
            this.employeeReportOptionId = $stateParams.employeeReportOptionId;
            this.reportName = 'Report';
            this.backLinkUrl = '/firm-management-reports';
        }

        /**********************************************************************
         * Method required by the report options controller to load report
         * options
         */
        public loadReportOptionsScreenData(): ng.IPromise<bluemoon.reporting.IReportEditOptionsScreenData> {
            return this.csReportOptionsV1.get({ reportDefinitionId: this.$stateParams.reportId });
        }

        /**********************************************************************
         * Method required by the report options controller to load previously
         * saved report data
         */
        public loadReportOptions(screenDataPromise: ng.IPromise<bluemoon.reporting.IReportEditOptionsScreenData>): ng.IPromise<bluemoon.reporting.IReportOptionsData> {
            var processData = (employeeReportOption: cs.IEmployeeReportOptionV1Dto) => {
                return screenDataPromise.then((screenData) => {
                    this.employeeReportOptionsDto = employeeReportOption;
                    this.reportName = employeeReportOption.reportDefinition.name;
                    return this.reportOptionsDataConverter.fromSaveFormat(angular.fromJson(employeeReportOption.defaultOptionsData), screenData);
                });
            };
            if (this.employeeReportOptionId && this.employeeReportOptionId.length > 0) {
                return this.currentEmployeeService.getCurrentEmployee().then((employee: cs.INamedReferenceWithCodeDto) => {
                    return this.csEmployeeReportOptionsV1
                        .get({ employeeId: employee.id, reportDefinitionId: this.reportId, employeeReportOptionId: this.employeeReportOptionId })
                        .then(processData);
                });
            } else {
                return this.currentEmployeeService.getCurrentEmployee().then((employee: cs.INamedReferenceWithCodeDto) => {
                    return this.firmManagementReportsListService.getReportList({}).then((list: cs.IPagedItemsDto<cs.IReportDefinitionBasicsV1Dto>) => {
                        let item = _.find(list.items, (item: cs.IReportDefinitionBasicsV1Dto) => {
                            return item.id === this.reportId;
                        });
                        if (item && item.employeeReportOptionList.length > 0) {
                            this.employeeReportOptionId = item.employeeReportOptionList[0];
                            return this.csEmployeeReportOptionsV1
                                .get({ employeeId: employee.id, reportDefinitionId: this.reportId, employeeReportOptionId: this.employeeReportOptionId })
                                .then(processData);
                        } else {
                            return this.csEmployeeReportOptionsV1
                                .getDefault({ employeeId: employee.id, reportDefinitionId: this.reportId })
                                .then(employeeReportOption => {
                                    // The get default endpoint should return no id. In that case, we just use 'NEW' so we have something
                                    if (!employeeReportOption.id) {
                                        employeeReportOption.id = this.providerUtils.newId();
                                    }
                                    return employeeReportOption;
                                }).then(processData);
                        }
                    });
                });
            }
        }

        public afterInitializeData(screenData: bluemoon.reporting.IReportEditOptionsScreenData, reportData: bmp.IReportOptionsData) {
            this.removeGroupByForFirmDetailBy(screenData, reportData);
        }

        public removeGroupByForFirmDetailBy(screenData: bluemoon.reporting.IReportEditOptionsScreenData, reportData: bmp.IReportOptionsData) {
            const pathPrefix = '.Company.FirmId';
            const stringEndsWith = (input: string, suffix: string) => input.indexOf(suffix, input.length - suffix.length) >= 0;

            if (screenData.detailByItems &&
                screenData.detailByItems.length > 0 &&
                screenData.detailByItems.some(i => stringEndsWith(i.path, pathPrefix)) &&
                reportData.detailBy) {
                var originalGroupByItems = screenData.groupByItems;
                var detailByPath = reportData.detailBy.path;
                reportData.detailBy.path = undefined;
                Object.defineProperty(reportData.detailBy, 'path', {
                    get: function() {
                        return detailByPath;
                    },
                    set: function(value) {
                        detailByPath = value;
                        if (stringEndsWith(value, pathPrefix)) {
                            reportData.groupBy = [];
                            screenData.groupByItems = null;
                        } else {
                            screenData.groupByItems = originalGroupByItems;
                        }
                    },
                    enumerable: true,
                    configurable: true
                });
                reportData.detailBy.path = detailByPath;
            }
        }

        /**********************************************************************
         * Save report options data for the current report and employee,
         * returning the employee report options id
         */
        saveReportOptions(reportOptions: bluemoon.reporting.IReportOptionsData, screenData: bluemoon.reporting.IReportEditOptionsScreenData): ng.IPromise<string> {
            var formattedReportOptions = this.reportOptionsDataConverter.toSaveFormat(reportOptions, screenData);
            this.employeeReportOptionsDto.defaultOptionsData = angular.toJson(formattedReportOptions);
            return this.currentEmployeeService.getCurrentEmployee().then((result: cs.INamedReferenceWithCodeDto) => {
                return (this.providerUtils.isNewId(this.employeeReportOptionsDto.id) ?
                    this.postEmployeeReportOptions(result.id) :
                    this.putEmployeeReportOptions(result.id))
                    .then((employeeOptionResult: cs.IEmployeeReportOptionV1Dto) => {
                        this.employeeReportOptionId = employeeOptionResult.id;
                        this.employeeReportOptionsDto = employeeOptionResult;
                        return this.employeeReportOptionsDto.defaultOptionsData;
                    });
            });
        }
        private postEmployeeReportOptions(employeeId: string) {
            return this.csEmployeeReportOptionsV1
                .post({ employeeId: employeeId, reportDefinitionId: this.reportId, data: this.employeeReportOptionsDto });
        }
        private putEmployeeReportOptions(employeeId: string) {
            return this.csEmployeeReportOptionsV1
                .put({
                    employeeId: employeeId, reportDefinitionId: this.reportId,
                    employeeReportOptionId: this.employeeReportOptionsDto.id, data: this.employeeReportOptionsDto
                });
        }

        public getSendReportToOnvioDocumentsUrl(): ng.IPromise<string> {
            return this.firmManagementReportsListService.getSendReportToOnvioDocumentsUrl();
        }

        public getSecuredReportDownloadUrl(): ng.IPromise<string> {
            return this.firmManagementReportsListService.getSecuredReportDownloadUrl();
        }

        public useNewAsyncMode(): boolean {
            return this.firmManagementReportsListService.useNewAsyncMode();
        }

        public processCreateDocumentRequestToSocketSrv(jobId: string, isSubscribe: boolean): void {
            this.firmManagementReportsListService.processCreateDocumentRequestToSocketSrv(jobId, isSubscribe);
        }

        public getCreateDocumentRequestUrl(): ng.IPromise<string> {
            return this.firmManagementReportsListService.getCreateDocumentRequestUrl();
        }

        public getSocketServicePromise(): ng.IPromise<any> {
            return this.firmManagementReportsListService.getSocketServicePromise();
        }

        public getCreateDocumentApipromise(): ng.IPromise<any> {
            return this.firmManagementReportsListService.getCreateDocumentApipromise();
        }

        public closeSocketConnection(): void {
            this.firmManagementReportsListService.closeSocketConnection();
        }

        public insertIntoSocketSrvTracing(jobId: string, payload: any): void {
            this.firmManagementReportsListService.insertIntoSocketSrvTracing(jobId, payload);
        }
    }

    angular.module('onvio.reports.firmManagementReports.FirmManagementReportsOptionsController',
        [
            'onvio.reports.firmManagementReports.FirmManagementReportsListService',
            'pascalprecht.translate',
            'bmpCurrentEmployeeService',
            'csReportOptionsV1',
            'csEmployeeReportOptionV1',
            'bmpCurrentEmployeeService',
            'bmpProviderUtils',
            'bmpEnumProvider.AccountsReceivableDebitCreditTypes',
            'bmrcReportListService',
            'bmpSelectProvider.Core',
            'bmpSelectProvider.ContactEntityType'
        ])

        .controller('onvio.reports.firmManagementReports.FirmManagementReportsOptionsController',
            FirmManagementReportsOptionsController);
}
